/**
 * EditorialHeatmap — generic 5-level intensity grid used by the BPA workload
 * view (5 columns + Health) and the App Posture permission view (4 columns).
 * Cell intensity classes l0-l4 map to a green→red ramp; l4.crit is solid red.
 */

import type { ReactNode } from 'react'

export type Intensity = 'l0' | 'l1' | 'l2' | 'l3' | 'l4'

export interface HeatmapCell {
  value: number | string
  intensity: Intensity
  /** When intensity = l4 and crit = true, render the solid-red treatment. */
  crit?: boolean
  /** Optional sub-label rendered below the value (e.g. units). */
  hint?: string
}

export interface HeatmapRow {
  /** Row label, e.g. "Identity". */
  label: string
  /** Optional second line, e.g. "42 controls". */
  sublabel?: string
  cells: HeatmapCell[]
  /** When provided, an extra "Health" column is rendered with the score and
   *  a 7d delta ("78% · ▲ +3"). */
  health?: { score: number; delta: number | null }
  /** Optional click target for the row. */
  href?: string
}

interface EditorialHeatmapProps {
  rows: HeatmapRow[]
  columns: string[]
  /** First column width in px. Default 200. */
  labelWidth?: number
  /** Override the empty-state node. */
  emptyState?: ReactNode
  /** When provided, non-zero cells become buttons that fire this callback. */
  onCellClick?: (rowIdx: number, colIdx: number, row: HeatmapRow, cell: HeatmapCell) => void
}

// Cell hues are themed via --heatmap-l{0..4}-{bg,fg} in index.css so the
// numerals stay legible against both the cream paper and the dark navy paper.
const INTENSITY_STYLE: Record<Intensity, { background: string; color: string }> = {
  l0: { background: 'var(--heatmap-l0-bg)', color: 'var(--heatmap-l0-fg)' },
  l1: { background: 'var(--heatmap-l1-bg)', color: 'var(--heatmap-l1-fg)' },
  l2: { background: 'var(--heatmap-l2-bg)', color: 'var(--heatmap-l2-fg)' },
  l3: { background: 'var(--heatmap-l3-bg)', color: 'var(--heatmap-l3-fg)' },
  l4: { background: 'var(--heatmap-l4-bg)', color: 'var(--heatmap-l4-fg)' },
}

const CRIT_STYLE = { background: 'rgba(200,75,75,0.85)', color: '#FFFFFF' }

export default function EditorialHeatmap({
  rows,
  columns,
  labelWidth = 200,
  emptyState,
  onCellClick,
}: EditorialHeatmapProps) {
  if (rows.length === 0) {
    return (
      <div
        className="
          rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)]
          bg-[color:var(--color-paper,#fff)] px-6 py-8 text-center text-[13px] italic
          text-[color:var(--color-mute,#6B7280)]
        "
      >
        {emptyState ?? 'No data to chart yet.'}
      </div>
    )
  }

  const hasHealth = rows.some(r => r.health != null)
  const cellCols = columns.length
  const trailingCols = hasHealth ? '120px' : ''
  const gridTemplate =
    `${labelWidth}px ${`repeat(${cellCols}, 1fr)`} ${trailingCols}`.trim()

  return (
    <div className="overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]">
      <div
        role="row"
        className="grid border-b border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)]"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
          Workload
        </div>
        {columns.map(c => (
          <div
            key={c}
            className="px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]"
          >
            {c}
          </div>
        ))}
        {hasHealth && (
          <div className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
            Health
          </div>
        )}
      </div>

      {rows.map((row, idx) => (
        <div
          key={`${row.label}-${idx}`}
          role="row"
          className="grid border-b border-[color:var(--color-rule,#E6E1D5)] last:border-b-0"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div className="flex flex-col justify-center px-4 py-3">
            <span className="text-[14px] font-semibold text-[color:var(--color-navy,#212938)]">
              {row.label}
            </span>
            {row.sublabel && (
              <span className="text-[11px] text-[color:var(--color-faint,#9CA3AF)]">
                {row.sublabel}
              </span>
            )}
          </div>

          {row.cells.map((cell, ci) => {
            const sty =
              cell.intensity === 'l4' && cell.crit
                ? CRIT_STYLE
                : INTENSITY_STYLE[cell.intensity]
            const numericValue =
              typeof cell.value === 'number'
                ? cell.value
                : Number.isFinite(Number(cell.value))
                  ? Number(cell.value)
                  : NaN
            const isClickable = onCellClick != null && Number.isFinite(numericValue) && numericValue > 0
            const cellInner = (
              <>
                <span className="font-display text-[18px] font-bold tabular-nums leading-none">
                  {cell.value}
                </span>
                {cell.hint && (
                  <span className="mt-0.5 text-[9px] uppercase tracking-[0.12em] opacity-70">
                    {cell.hint}
                  </span>
                )}
              </>
            )
            if (isClickable) {
              return (
                <button
                  key={ci}
                  type="button"
                  role="cell"
                  onClick={() => onCellClick!(idx, ci, row, cell)}
                  className="m-1 flex cursor-pointer flex-col items-center justify-center rounded-[3px] py-3 transition-shadow hover:shadow-[inset_0_0_0_1.5px_rgba(33,41,56,0.45)] focus:outline-none focus-visible:shadow-[inset_0_0_0_1.5px_rgba(33,41,56,0.85)]"
                  style={sty}
                  aria-label={`Open ${row.label} ${columns[ci]} in deep dive`}
                >
                  {cellInner}
                </button>
              )
            }
            return (
              <div
                key={ci}
                role="cell"
                className="m-1 flex flex-col items-center justify-center rounded-[3px] py-3"
                style={sty}
              >
                {cellInner}
              </div>
            )
          })}

          {hasHealth && (
            <div className="flex flex-col items-end justify-center gap-0.5 px-4 py-3">
              {row.health ? (
                <>
                  <span className="font-display text-[18px] font-bold tabular-nums text-[color:var(--color-navy,#212938)]">
                    {Math.round(row.health.score)}%
                  </span>
                  <span
                    className={[
                      'text-[10px] font-semibold tabular-nums',
                      row.health.delta == null
                        ? 'text-[color:var(--color-faint,#9CA3AF)]'
                        : row.health.delta > 0
                        ? 'text-[#3F8259]'
                        : row.health.delta < 0
                        ? 'text-[#A03434]'
                        : 'text-[color:var(--color-mute,#6B7280)]',
                    ].join(' ')}
                  >
                    {row.health.delta == null
                      ? '— 7d'
                      : `${row.health.delta > 0 ? '▲ +' : row.health.delta < 0 ? '▼ ' : '·'}${Math.abs(row.health.delta)} 7d`}
                  </span>
                </>
              ) : (
                <span className="text-[11px] italic text-[color:var(--color-faint,#9CA3AF)]">
                  — 7d
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
